import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CarouselModule } from 'primeng/carousel';
import { DividerModule } from 'primeng/divider';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';

import { TasksService } from '../../services/tasks/tasks.service';

@Component({
  selector: 'app-task-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CarouselModule,
    DividerModule,
    InputTextareaModule,
    TooltipModule,
    NgxDropzoneModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './task-details.component.html',
  styleUrls: ['./task-details.component.scss']
})
export class TaskDetailsComponent implements OnInit, OnDestroy {
  private _tasksService = inject(TasksService);
  private _messageService = inject(MessageService);
  public config = inject(DynamicDialogConfig);
  public ref = inject(DynamicDialogRef);

  task: any;
  photos: any[] = [];
  loading: boolean = false;

  newPhotoFile: File | null = null;
  newPhotoPreview: string = '';
  photoComment: string = '';
  uploadingPhoto: boolean = false;

  taskHistory: any[] = [];
  newComment: string = '';
  addingComment: boolean = false;
  historyPagination = {
    page: 1,
    page_size: 5,
    total: 0,
    loading: false
  };

  expandedCommentId: string | null = null;
  commentPhotos: { [key: string]: any[] } = {};
  newCommentPhotoFile: { [key: string]: File | null } = {};
  newCommentPhotoPreview: { [key: string]: string } = {};
  commentPhotoComment: { [key: string]: string } = {};
  uploadingCommentPhoto: { [key: string]: boolean } = {};

  ngOnInit(): void {
    this.task = this.config.data.task;
    this.loadTaskPhotos();
    this.loadTaskHistory();
  }

  ngOnDestroy(): void {
    if (this.newPhotoPreview) {
      URL.revokeObjectURL(this.newPhotoPreview);
    }

    Object.values(this.newCommentPhotoPreview).forEach(url => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });
  }

  loadTaskPhotos(): void {
    if (!this.task?.id) {
      return;
    }

    this.loading = true;

    this._tasksService.getTaskPhotos(this.task.id).subscribe({
      next: (response: any) => {
        this.photos = response.items || response;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading task photos:', err);
        this.loading = false;
      }
    });
  }

  onSelect(event: any): void {
    if (event.addedFiles && event.addedFiles.length > 0) {
      this.newPhotoFile = event.addedFiles[0];
      if (this.newPhotoFile) {
        this.newPhotoPreview = URL.createObjectURL(this.newPhotoFile);
      }
    }
  }

  onRemoveNewPhoto(): void {
    if (this.newPhotoPreview) {
      URL.revokeObjectURL(this.newPhotoPreview);
    }

    this.newPhotoFile = null;
    this.newPhotoPreview = '';
  }

  uploadPhoto(): void {
    if (!this.newPhotoFile) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Por favor, selecciona una foto'
      });

      return;
    }

    if (!this.photoComment.trim()) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Por favor, agrega un comentario'
      });

      return;
    }

    this.uploadingPhoto = true;

    const formData = new FormData();
    formData.append('photo', this.newPhotoFile);
    formData.append('task', this.task.id);
    formData.append('comment', this.photoComment);

    this._tasksService.createTaskPhoto(formData).subscribe({
      next: (response) => {
        this._messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Foto agregada correctamente'
        });

        this.loadTaskPhotos();
        this.onRemoveNewPhoto();

        this.photoComment = '';
        this.uploadingPhoto = false;
      },
      error: (err) => {
        console.error('Error uploading photo:', err);
        this._messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al subir la foto'
        });
        this.uploadingPhoto = false;
      }
    });
  }

  deletePhoto(photo: any): void {
    if (confirm('¿Está seguro que desea eliminar esta foto?')) {
      this._tasksService.deleteTaskPhoto(photo.id).subscribe({
        next: () => {
          this._messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Foto eliminada correctamente'
          });

          this.loadTaskPhotos();
        },
        error: (err) => {
          console.error('Error deleting photo:', err);
          this._messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al eliminar la foto'
          });
        }
      });
    }
  }

  getStatusClass(status: any): string {
    if (status?.key_string === 'task-todo') return 'status-red';
    if (status?.key_string === 'task-inprogress') return 'status-yellow';
    if (status?.key_string === 'task-done') return 'status-green';
    return 'status-blue';
  }

  loadTaskHistory(): void {
    if (!this.task?.id) {
      return;
    }

    this.historyPagination.loading = true;

    this._tasksService.getTaskHistory(
      this.task.id,
      this.historyPagination.page,
      this.historyPagination.page_size
    ).subscribe({
      next: (response: any) => {
        const newComments = response.items || response.results || [];

        if (this.historyPagination.page === 1) {
          this.taskHistory = newComments;
        } else {
          this.taskHistory = [...this.taskHistory, ...newComments];
        }

        this.historyPagination.total = response.total || response.count || 0;
        this.historyPagination.loading = false;
      },
      error: (err) => {
        console.error('Error loading task history:', err);
        this.historyPagination.loading = false;
      }
    });
  }

  addComment(): void {
    if (!this.newComment.trim()) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Por favor, escribe un comentario'
      });

      return;
    }

    this.addingComment = true;

    const data = {
      task: this.task.id,
      comments: this.newComment
    };

    this._tasksService.createTaskHistory(data).subscribe({
      next: (response) => {
        this._messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Comentario agregado correctamente'
        });

        this.newComment = '';
        this.historyPagination.page = 1;
        this.loadTaskHistory();
        this.addingComment = false;
      },
      error: (err) => {
        console.error('Error adding comment:', err);
        this._messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al agregar el comentario'
        });
        this.addingComment = false;
      }
    });
  }

  loadMoreComments(): void {
    if (this.historyPagination.page * this.historyPagination.page_size >= this.historyPagination.total) {
      return;
    }

    this.historyPagination.page++;
    this.loadTaskHistory();
  }

  toggleCommentPhotos(commentId: string): void {
    if (this.expandedCommentId === commentId) {
      this.expandedCommentId = null;
    } else {
      this.expandedCommentId = commentId;
      if (!this.commentPhotos[commentId]) {
        this.loadCommentPhotos(commentId);
      }
    }
  }

  loadCommentPhotos(commentId: string): void {
    this._tasksService.getTaskHistoryPhotos(commentId).subscribe({
      next: (response: any) => {
        this.commentPhotos[commentId] = response.items || response;
      },
      error: (err) => {
        console.error('Error loading comment photos:', err);
      }
    });
  }

  onSelectCommentPhoto(event: any, commentId: string): void {
    if (event.addedFiles && event.addedFiles.length > 0) {
      this.newCommentPhotoFile[commentId] = event.addedFiles[0];
      this.newCommentPhotoPreview[commentId] = URL.createObjectURL(event.addedFiles[0]);
    }
  }

  onRemoveCommentPhoto(commentId: string): void {
    if (this.newCommentPhotoPreview[commentId]) {
      URL.revokeObjectURL(this.newCommentPhotoPreview[commentId]);
    }

    this.newCommentPhotoFile[commentId] = null;
    this.newCommentPhotoPreview[commentId] = '';
  }

  uploadCommentPhoto(commentId: string): void {
    if (!this.newCommentPhotoFile[commentId]) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Por favor, selecciona una foto'
      });

      return;
    }

    this.uploadingCommentPhoto[commentId] = true;

    const formData = new FormData();
    formData.append('photo', this.newCommentPhotoFile[commentId]!);
    formData.append('taskHistory', commentId);

    if (this.commentPhotoComment[commentId]?.trim()) {
      formData.append('comment', this.commentPhotoComment[commentId]);
    }

    this._tasksService.createTaskHistoryPhoto(formData).subscribe({
      next: (response) => {
        this._messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Foto agregada correctamente'
        });

        this.loadCommentPhotos(commentId);
        this.onRemoveCommentPhoto(commentId);

        this.commentPhotoComment[commentId] = '';
        this.uploadingCommentPhoto[commentId] = false;
      },
      error: (err) => {
        console.error('Error uploading comment photo:', err);
        this._messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al subir la foto'
        });
        this.uploadingCommentPhoto[commentId] = false;
      }
    });
  }

  deleteComment(comment: any): void {
    if (confirm('¿Está seguro que desea eliminar este comentario?')) {
      this._tasksService.deleteTaskHistory(comment.id).subscribe({
        next: () => {
          this._messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Comentario eliminado correctamente'
          });

          this.historyPagination.page = 1;
          this.loadTaskHistory();
        },
        error: (err) => {
          console.error('Error deleting comment:', err);
          this._messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al eliminar el comentario'
          });
        }
      });
    }
  }

  deleteCommentPhoto(photo: any, commentId: string): void {
    if (confirm('¿Está seguro que desea eliminar esta foto?')) {
      this._tasksService.deleteTaskHistoryPhoto(photo.id).subscribe({
        next: () => {
          this._messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Foto eliminada correctamente'
          });
          this.loadCommentPhotos(commentId);
        },
        error: (err) => {
          console.error('Error deleting comment photo:', err);
          this._messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al eliminar la foto'
          });
        }
      });
    }
  }

  close(): void {
    this.ref.close();
  }
}
